// The three things an account cannot be created without, and the one place their wording
// lives.
//
// Wave C item C3, finding F13. This used to be a single `consentAccepted` checkbox whose
// label read "I consent to the collection and processing of my personal data as described
// in the Terms & Conditions and Privacy Policy, and confirm that I am at least 18 years
// old." One tick, three different kinds of thing:
//
//   - the DPDP notice is CONSENT, and the only one of the three that is;
//   - the Terms are a CONTRACT you accept, not consent you give;
//   - the age declaration is a STATEMENT OF FACT under s9.
//
// Rule 3 requires the notice to be presented independently of other information, and
// s6(1) limits consent to what the specified purpose needs. Bundling them also made
// withdrawal incoherent - there was one bit to withdraw, covering all three, so C1 could
// not have acted on the right one even if it had wanted to.
//
// Keyed by the server's consent purposes so the two cannot drift: these are the same
// strings `utils/consentPurposes.js` derives its signup set from, and the server refuses
// each by name.
export const SIGNUP_ACKNOWLEDGEMENTS = [
  {
    key: 'dpdp_notice',
    label: 'I consent to Eternal Locker processing my personal data as described in the notice above.',
    // What to CALL this box in a sentence about it. `label` is the first-person wording of
    // the box itself and only works next to the tickbox; a refusal that reprints it makes
    // the app say "I consent..." back at the person who declined to (EL-33).
    shortName: 'the privacy notice',
    hint: 'This is your DPDP consent. You can withdraw it later from Profile › Privacy Choices.',
  },
  {
    key: 'terms',
    label: 'I accept the Terms & Conditions.',
    shortName: 'the Terms & Conditions',
    hint: 'A contract between you and Nybble Technologies, separate from the consent above.',
    href: '/terms',
    hrefLabel: 'Read the Terms & Conditions',
  },
  {
    key: 'age_declaration',
    label: 'I confirm I am at least 18 years old.',
    shortName: 'the 18+ declaration',
    hint: 'Eternal Locker is not offered to children. Your date of birth is checked against this.',
  },
];


export const emptyAcknowledgements = () => Object.fromEntries(
  SIGNUP_ACKNOWLEDGEMENTS.map(({ key }) => [key, false])
);

export const allAcknowledged = (acknowledgements) =>
  SIGNUP_ACKNOWLEDGEMENTS.every(({ key }) => acknowledgements?.[key] === true);

// Used for the client-side message. The server sends back a `missing` array of the same
// keys, so a refusal can be pointed at the exact box either way.
export const missingAcknowledgements = (acknowledgements) =>
  SIGNUP_ACKNOWLEDGEMENTS.filter(({ key }) => acknowledgements?.[key] !== true);

// "a", "a and b", "a, b and c" - so a refusal naming one outstanding box reads as well as
// one naming three. Joining with a bare separator is what produced the run-on sentence
// EL-33 was raised about.
export const formatList = (items) => {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

// The whole refusal, in one place, because it is the sentence a signing-up user is most
// likely to see. It names the boxes rather than quoting them, and it says what ticking
// them is for - "please accept: <three first-person sentences>" did neither.
export const acknowledgementRefusal = (acknowledgements) => {
  const outstanding = missingAcknowledgements(acknowledgements);
  if (outstanding.length === 0) return null;
  return `Please tick ${formatList(outstanding.map(({ shortName }) => shortName))} to create your account.`;
};
